import React, { useEffect, useState, useRef, useCallback } from 'react';
import { host, formatDateTime } from './script/variables';
import OrderPaymentStatus from './partner/OrderPaymentStatus';
import Nav from './common/Nav';
import { generateInvoice } from './GenerateInvoice';
import './style/orderlist.css';
import BottomNav from './BottomNav';
import Login from './Login';

/* ── status tab config ── */
const TABS = [
  { label: 'Active',    statuses: ['Pending', 'Preparing', 'Ready', 'Hold'], icon: '🟡' },
  { label: 'Completed', statuses: ['Completed'],                             icon: '✅' },
  { label: 'Cancelled', statuses: ['Cancelled'],                             icon: '❌' },
];

/* ── delivery status styling ── */
const STATUS_META = {
  Pending:   { label: 'Pending',   cls: 'ol-ds-pending'   },
  Preparing: { label: 'Preparing', cls: 'ol-ds-preparing' },
  Ready:     { label: 'Ready',     cls: 'ol-ds-ready'     },
  Hold:      { label: 'On Hold',   cls: 'ol-ds-hold'      },
  Completed: { label: 'Completed', cls: 'ol-ds-completed' },
  Cancelled: { label: 'Cancelled', cls: 'ol-ds-cancelled' },
};

const PAGE_SIZE = 20;

/* ─────────────────────────────────────────────────────────────────
   Invoice button – shown only when both statuses are "Completed"
───────────────────────────────────────────────────────────────── */
const InvoiceButton = ({ order }) => {
  const [busy, setBusy] = useState(false);

  const eligible =
    order.deliveryStatus === 'Completed' &&
    order.paymentStatus?.toLowerCase() === 'completed';

  if (!eligible) return null;

  const handleClick = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await generateInvoice(order);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      className={`ol-invoice-btn ${busy ? 'ol-invoice-btn--busy' : ''}`}
      onClick={handleClick}
      title="Download Invoice PDF"
      aria-label="Download Invoice"
    >
      {busy ? (
        <span className="ol-inv-spinner" />
      ) : (
        <svg viewBox="0 0 20 20" fill="none" width="14" height="14">
          <path d="M10 2v9m0 0l-3-3m3 3l3-3" stroke="currentColor"
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M3 14v2a1 1 0 001 1h12a1 1 0 001-1v-2"
            stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      )}
      <span>{busy ? 'Generating…' : 'Invoice'}</span>
    </button>
  );
};

/* ─────────────────────────────────────────────────────────────────
   Login prompt – shown when user is not authenticated
───────────────────────────────────────────────────────────────── */
const LoginPrompt = ({ setIsLogin }) => (
  <div className="ol-login-prompt">
    <div className="ol-login-prompt-icon">🔐</div>
    <p className="ol-login-prompt-title">Login to see your orders</p>
    <p className="ol-login-prompt-sub">
      Your order history will appear here once you&apos;re logged in.
    </p>
    <div className="ol-login-prompt-btn">
      <Login setIsLogin={setIsLogin} />
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   Main component
───────────────────────────────────────────────────────────────── */
const OrderList = ({ socket }) => {
  const [activeTab,    setActiveTab]    = useState(0);
  const [orders,       setOrders]       = useState([]);
  const [pno,          setPno]          = useState(1);
  const [hasMore,      setHasMore]      = useState(true);
  const [loading,      setLoading]      = useState(false);
  const [loadingMore,  setLoadingMore]  = useState(false);
  const [error,        setError]        = useState('');
  const [expanded,     setExpanded]     = useState(null);
  const [changeStatus, setChangeStatus] = useState(false);
  const [isLogin,      setIsLogin]      = useState(false);
  const [authChecked,  setAuthChecked]  = useState(false);
  const sentinelRef = useRef(null);
  const status = TABS[activeTab].statuses;


  /* ── auth check — runs once on mount ── */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${host}/user/vlogin`, {
          method: 'GET', headers: { 'Content-Type': 'application/json', Authorization: localStorage.getItem('token') },
        });
        if (alive) setIsLogin(res.ok);
      } catch (e) {
        console.error(e);
        if (alive) setIsLogin(false);
      } finally {
        if (alive) setAuthChecked(true);
      }
    })();
    return () => { alive = false; };
  }, []);


  /* ── fetch ── */
  const fetchOrders = useCallback(async (page, statusArr) => {
    if (page === 1) setLoading(true);
    else            setLoadingMore(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${host}/order/fetchCustomerorders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ pno: page, status: statusArr }),
      });
      if (!res.ok) throw new Error('fetch failed');
      const data = await res.json();
      setOrders(prev => page === 1 ? data : [...prev, ...data]);
      setPno(page + 1);
      setHasMore(data.length >= PAGE_SIZE);
    } catch {
      setError('Could not load orders. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  /* ── reset + fetch on tab change — only when logged in ── */
  useEffect(() => {
    if (!isLogin) return;

    setOrders([]);
    setPno(1);
    setHasMore(true);
    setExpanded(null);
    fetchOrders(1, TABS[activeTab].statuses);
  }, [activeTab, isLogin]);

  /* ── infinite scroll — only when logged in ── */
  useEffect(() => {
    if (!isLogin || !sentinelRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loadingMore && !loading)
          fetchOrders(pno, status);
      },
      { rootMargin: '180px' }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [isLogin, hasMore, loadingMore, loading, pno, fetchOrders, status]);

  const toggleExpand = (id) => setExpanded(prev => (prev === id ? null : id));

  /* ── skeleton ── */
  const Skeleton = () => (
    <div className="ol-skeleton-list">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="ol-skeleton-card">
          <div className="ol-sk ol-sk-top" />
          <div className="ol-sk ol-sk-mid" />
          <div className="ol-sk ol-sk-bot" />
        </div>
      ))}
    </div>
  );

  return (
    <>
      <Nav isLogin={isLogin} setIsLogin={setIsLogin} vegMode={false} />
      <div className="ol-shell">

        {/* ── Header ── */}
        <div className="ol-header">
          <h1 className="ol-header-title">My Orders</h1>
          <p className="ol-header-sub">Track and review all your orders</p>
        </div>

        {/* ── Not logged in ── */}
        {authChecked && !isLogin && <LoginPrompt setIsLogin={setIsLogin} />}

        {/* ── Logged in content ── */}
        {isLogin && (
          <>
            {/* ── Tabs ── */}
            <div className="ol-tabs">
              {TABS.map((tab, i) => (
                <button
                  key={tab.label}
                  className={`ol-tab ${activeTab === i ? 'ol-tab-on' : ''}`}
                  onClick={() => setActiveTab(i)}
                >
                  <span className="ol-tab-icon">{tab.icon}</span>
                  {tab.label}
                  {activeTab === i && orders.length > 0 && (
                    <span className="ol-tab-count">{orders.length}</span>
                  )}
                </button>
              ))}
            </div>

            {/* ── Content ── */}
            <div className="ol-content">

              {error && (
                <div className="ol-error">
                  <span>{error}</span>
                  <button onClick={() => fetchOrders(pno, status)}>Retry</button>
                </div>
              )}

              {loading && <Skeleton />}

              {!loading && orders.length === 0 && !error && (
                <div className="ol-empty">
                  <div className="ol-empty-icon">{TABS[activeTab].icon}</div>
                  <p className="ol-empty-title">No {TABS[activeTab].label.toLowerCase()} orders</p>
                  <p className="ol-empty-sub">
                    {activeTab === 0
                      ? "You don't have any active orders right now."
                      : `Your ${TABS[activeTab].label.toLowerCase()} orders will appear here.`}
                  </p>
                </div>
              )}

              {!loading && orders.length > 0 && (
                <div className="ol-list">
                  {orders.map(order => {
                    const rest   = order.restaurantId;
                    const dsMeta = STATUS_META[order.deliveryStatus] ?? { label: order.deliveryStatus, cls: '' };
                    const isOpen = expanded === order._id;
                    const isDone = order.deliveryStatus === 'Completed' || order.deliveryStatus === 'Cancelled';

                    return (
                      <div key={order._id} className={`ol-card ${isOpen ? 'ol-card-open' : ''}`}>

                        {/* ── Card header ── */}
                        <button
                          className="ol-card-head"
                          onClick={() => toggleExpand(order._id)}
                          aria-expanded={isOpen}
                        >
                          <div className="ol-rest-avatar">
                            {rest?.thumbnail
                              ? <img src={rest.thumbnail} alt={rest?.name} />
                              : <span>{rest?.name?.[0]?.toUpperCase() ?? '?'}</span>
                            }
                          </div>

                          <div className="ol-card-info">
                            <div className="ol-card-row1">
                              <span className="ol-rest-name">{rest?.name ?? 'Restaurant'}</span>
                              <span className="ol-card-price">₹{order.totalPrice}</span>
                            </div>
                            <div className="ol-card-row2">
                              <span className="ol-order-num">#{order.dailyOrderNumber}</span>
                              <span className="ol-order-type">{order.orderType}</span>
                              <span className="ol-dot">·</span>
                              <span className="ol-placed-at">{formatDateTime(order.placedAt)}</span>
                            </div>
                            <div className="ol-card-row3">
                              <span className={`ol-ds-pill ${dsMeta.cls}`}>{dsMeta.label}</span>
                              <span className={`ol-pay-pill ol-pay-${order.paymentStatus?.toLowerCase()}`}>
                                {order.paymentStatus}
                              </span>
                              <InvoiceButton order={order} />
                            </div>
                          </div>

                          <svg
                            className={`ol-chevron ${isOpen ? 'ol-chevron-open' : ''}`}
                            viewBox="0 0 20 20" fill="none"
                          >
                            <path d="M5 7.5l5 5 5-5" stroke="currentColor"
                              strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>

                        {/* ── Expanded details ── */}
                        {isOpen && (
                          <div className="ol-card-body">

                            {rest?.address && (
                              <div className="ol-rest-address">
                                <svg viewBox="0 0 20 20" fill="none" width="13" height="13">
                                  <path d="M10 2a6 6 0 016 6c0 4-6 10-6 10S4 12 4 8a6 6 0 016-6z"
                                    stroke="currentColor" strokeWidth="1.6" />
                                  <circle cx="10" cy="8" r="2" stroke="currentColor" strokeWidth="1.6" />
                                </svg>
                                <span>{rest.address}</span>
                              </div>
                            )}

                            <div className="ol-items">
                              <p className="ol-items-label">Items ordered</p>
                              {order.items.map((item, i) => (
                                <div key={i} className="ol-item-row">
                                  <span className="ol-item-name">{item.item}</span>
                                  {item.isTaxable && (
                                    <span className="ol-item-gst-tag" title="GST applicable">GST</span>
                                  )}
                                  <span className="ol-item-qty">× {item.quantity}</span>
                                  <span className="ol-item-price">
                                    ₹{Math.round(item.price * item.quantity * (1 - (item.discount || 0) / 100))}
                                    {item.discount > 0 && (
                                      <span className="ol-item-disc">{item.discount}% off</span>
                                    )}
                                  </span>
                                </div>
                              ))}

                              {order.combos?.map((combo, i) => (
                                <div key={`combo-${i}`} className="ol-combo-block">
                                  <div className="ol-item-row">
                                    <span className="ol-item-name">{combo.name}</span>
                                    {combo.isTaxable && (
                                      <span className="ol-item-gst-tag" title="GST applicable">GST</span>
                                    )}
                                    <span className="ol-item-qty">× {combo.quantity}</span>
                                    <span className="ol-item-price">₹{combo.totalPrice}</span>
                                  </div>
                                  <div className="ol-combo-dishes">
                                    {combo.dishes?.map((d, di) => (
                                      <div key={di} className="ol-combo-dish-row">
                                        <span className="ol-combo-dish-name">
                                          {d.name}{d.size ? ` (${d.size.charAt(0).toUpperCase()}${d.size.slice(1)})` : ''}
                                        </span>
                                        <span className="ol-combo-dish-qty">× {d.quantity}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                              <div className="ol-items-total">
                                <span>Total</span>
                                <span>₹{order.totalPrice}</span>
                              </div>
                            </div>

                            {order.specialInstructions && (
                              <div className="ol-instructions">
                                <p className="ol-instructions-label">Special instructions</p>
                                <p className="ol-instructions-text">{order.specialInstructions}</p>
                              </div>
                            )}

                            <div className="ol-timestamps">
                              <div className="ol-ts-row">
                                <span className="ol-ts-label">Placed at</span>
                                <span className="ol-ts-val">{formatDateTime(order.placedAt)}</span>
                              </div>
                              {isDone && order.completedAt && (
                                <div className="ol-ts-row">
                                  <span className="ol-ts-label">{order.deliveryStatus} at</span>
                                  <span className="ol-ts-val">{formatDateTime(order.completedAt)}</span>
                                </div>
                              )}
                              <div className="ol-ts-row">
                                <span className="ol-ts-label">Payment method</span>
                                <span className="ol-ts-val">{order.paymentMethod}</span>
                              </div>
                            </div>

                            <div className="ol-payment-wrap">
                              <OrderPaymentStatus
                                orderid={order._id}
                                changestatus={changeStatus}
                                setChangeStatus={setChangeStatus}
                              />
                            </div>

                            <div className="ol-invoice-expanded-wrap">
                              <InvoiceButton order={order} />
                            </div>

                            <p className="ol-order-id">Order ID: {order._id}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div ref={sentinelRef} />
              {loadingMore && (
                <div className="ol-load-more">
                  <span className="ol-spinner" />
                  Loading more…
                </div>
              )}
              {!hasMore && !loadingMore && orders.length > 0 && (
                <p className="ol-end-msg">You've seen all your orders 🎉</p>
              )}
            </div>
          </>
        )}
<BottomNav/>
      </div>
    </>
  );
};

export default OrderList;